import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Button from "@components/Button";
import Input from "@components/Input";
import Spinner from "@components/Spinner";
import {
  DefaultAnalyticsSettings,
  getDefaultAnalyticsSettings,
  updateDefaultAnalyticsSettings,
  DEFAULT_ANALYTICS_SETTINGS,
  mergePotatoParams,
} from "@services/adminService";

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: bold;
  color: #899093;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const Card = styled.div`
  background: #fafafa;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 16px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const ActionButton = styled(Button)`
  width: auto;
  min-width: 100px;
`;

const Message = styled.div<{ error?: boolean }>`
  margin-top: 12px;
  font-size: 13px;
  color: ${({ error }) => (error ? "#e74c3c" : "#68ad18")};
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const Select = styled.select`
  height: 44px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  padding: 0 12px;
  color: #494c5b;
  outline: none;
  background: white;
  width: 100%;
  box-sizing: border-box;
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DefaultAnalyticsSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<DefaultAnalyticsSettings | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(
    null
  );

  // A failed load shows the built-in defaults, but saving them would overwrite
  // the real global settings (and their potato `changes`) — so no save.
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDefaultAnalyticsSettings();
      setSettings(data);
      setLoadFailed(false);
    } catch (e) {
      setSettings(DEFAULT_ANALYTICS_SETTINGS);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    if (!settings) return;
    setMessage(null);
    setSaving(true);
    try {
      await updateDefaultAnalyticsSettings(settings);
      setMessage({ text: "Saved successfully", error: false });
      fetchSettings();
    } catch (e: any) {
      setMessage({ text: e.message || "Failed to save", error: true });
    } finally {
      setSaving(false);
    }
  };

  const setRetrofit = (updates: Partial<DefaultAnalyticsSettings["retrofit"]>) => {
    setSettings((prev) =>
      prev ? { ...prev, retrofit: { ...prev.retrofit, ...updates } } : null
    );
  };

  const setPotatoBase = (
    updates: Partial<Omit<DefaultAnalyticsSettings["potato"], "params_default">>
  ) => {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            potato: { ...prev.potato, ...updates },
          }
        : null
    );
  };

  const setPotatoParams = (
    updates: Partial<DefaultAnalyticsSettings["potato"]["params_default"]>
  ) => {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            potato: {
              ...prev.potato,
              params_default: mergePotatoParams({
                ...(prev.potato.params_default ?? {}),
                ...updates,
              }),
            },
          }
        : null
    );
  };

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  if (!settings) return null;

  return (
    <div>
      <Section>
        <SectionTitle>Default analytics settings</SectionTitle>
        <Text size={13} color="#899093" style={{ marginBottom: 16 }}>
          These settings apply to every new device when it is created. Existing
          devices keep their current settings.
        </Text>
      </Section>

      {/* Retrofit/CAN defaults */}
      <Card>
        <SectionTitle>Retrofit / CAN defaults</SectionTitle>
        <FormGrid>
          <FieldWrap>
            <Text size={12} color="#899093">
              Min speed (km/h)
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.retrofit.min_speed}
              onChange={(e) =>
                setRetrofit({ min_speed: parseFloat(e.target.value) || 0 })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Max speed (km/h)
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.retrofit.max_speed}
              onChange={(e) =>
                setRetrofit({ max_speed: parseFloat(e.target.value) || 0 })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Delay (s)
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.retrofit.delay_s}
              onChange={(e) =>
                setRetrofit({ delay_s: parseFloat(e.target.value) || 0 })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Delay tolerance
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.retrofit.delay_tolerance}
              onChange={(e) =>
                setRetrofit({ delay_tolerance: parseFloat(e.target.value) || 0 })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Lateral side
            </Text>
            <Select
              value={settings.retrofit.lateral_side}
              onChange={(e) =>
                setRetrofit({ lateral_side: e.target.value as "R" | "L" })
              }
            >
              <option value="R">R</option>
              <option value="L">L</option>
            </Select>
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Lateral shift (m)
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.retrofit.lateral_shift_m}
              onChange={(e) =>
                setRetrofit({ lateral_shift_m: parseFloat(e.target.value) || 0 })
              }
            />
          </FieldWrap>
        </FormGrid>
      </Card>

      {/* Potato defaults */}
      <Card>
        <SectionTitle>Potato (global)</SectionTitle>
        <Text size={12} color="#899093" style={{ marginBottom: 12 }}>
          Base parameters and initial potato yield settings applied to new potato devices.
        </Text>
        <FormGrid>
          <FieldWrap>
            <Text size={12} color="#899093">
              Min speed (km/h)
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.potato.min_speed}
              onChange={(e) =>
                setPotatoBase({ min_speed: parseFloat(e.target.value) || 0 })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Max speed (km/h)
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.potato.max_speed}
              onChange={(e) =>
                setPotatoBase({ max_speed: parseFloat(e.target.value) || 0 })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Delay (s)
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.potato.delay_s}
              onChange={(e) =>
                setPotatoBase({ delay_s: parseFloat(e.target.value) || 0 })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Delay tolerance
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.potato.delay_tolerance}
              onChange={(e) =>
                setPotatoBase({
                  delay_tolerance: parseFloat(e.target.value) || 0,
                })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Lateral side
            </Text>
            <Select
              value={settings.potato.lateral_side}
              onChange={(e) =>
                setPotatoBase({ lateral_side: e.target.value as "R" | "L" })
              }
            >
              <option value="R">R</option>
              <option value="L">L</option>
            </Select>
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Lateral shift (m)
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.potato.lateral_shift_m}
              onChange={(e) =>
                setPotatoBase({
                  lateral_shift_m: parseFloat(e.target.value) || 0,
                })
              }
            />
          </FieldWrap>
        </FormGrid>

        <SectionTitle style={{ marginTop: 20 }}>
          Initial potato yield parameters
        </SectionTitle>
        <FormGrid>
          <FieldWrap>
            <Text size={12} color="#899093">
              Parameter
            </Text>
            <Select
              value={settings.potato.params_default.parameter}
              onChange={(e) =>
                setPotatoParams({
                  parameter: e.target.value as "sum" | "value" | "freq",
                })
              }
            >
              <option value="sum">sum</option>
              <option value="value">value</option>
              <option value="freq">freq</option>
            </Select>
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Baseline
            </Text>
            <Select
              value={
                settings.potato.params_default.baseline === "dynamic"
                  ? "dynamic"
                  : String(settings.potato.params_default.baseline)
              }
              onChange={(e) =>
                setPotatoParams({
                  baseline:
                    e.target.value === "dynamic"
                      ? "dynamic"
                      : parseFloat(e.target.value) || 0,
                })
              }
            >
              <option value="0">0</option>
              <option value="dynamic">dynamic</option>
            </Select>
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Baseline value
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.potato.params_default.baseline_value}
              onChange={(e) =>
                setPotatoParams({
                  baseline_value: parseFloat(e.target.value) || 0,
                })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Baseline freq
            </Text>
            <Input
              type="number"
              step="0.1"
              value={settings.potato.params_default.baseline_freq}
              onChange={(e) =>
                setPotatoParams({
                  baseline_freq: parseFloat(e.target.value) || 0,
                })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Reaper (-1 to 100)
            </Text>
            <Input
              type="number"
              min={-1}
              max={100}
              value={settings.potato.params_default.reaper}
              onChange={(e) =>
                setPotatoParams({
                  reaper: Math.min(
                    100,
                    Math.max(-1, parseInt(e.target.value, 10) || -1)
                  ),
                })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Delta min
            </Text>
            <Input
              type="number"
              value={settings.potato.params_default.delta_min}
              onChange={(e) =>
                setPotatoParams({
                  delta_min: parseInt(e.target.value, 10) || 0,
                })
              }
            />
          </FieldWrap>
          <FieldWrap>
            <Text size={12} color="#899093">
              Delta max
            </Text>
            <Input
              type="number"
              value={settings.potato.params_default.delta_max}
              onChange={(e) =>
                setPotatoParams({
                  delta_max: parseInt(e.target.value, 10) || 0,
                })
              }
            />
          </FieldWrap>
        </FormGrid>
      </Card>

      <Actions>
        <ActionButton onClick={handleSave} disabled={saving || loadFailed}>
          {saving ? "Saving..." : "Save"}
        </ActionButton>
      </Actions>
      {message && <Message error={message.error}>{message.text}</Message>}
    </div>
  );
};

export default DefaultAnalyticsSettingsTab;
